import React, { useState } from "react";
import axios from "axios";
import { Linkedin, ArrowUpRight, Loader2, Check, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { track } from "../../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const interests = [
  "Strategy Audit",
  "Frontline Elite (Retail)",
  "Rookie Hunter (B2B)",
  "Master Hunter Pro",
  "Elite 1-on-1 Mentorship",
  "BDaaS / Fractional Leadership",
  "Custom Tech Build",
];

const COURSE_INTERESTS = new Set([
  "Frontline Elite (Retail)",
  "Rookie Hunter (B2B)",
  "Master Hunter Pro",
  "Elite 1-on-1 Mentorship",
]);

const isCourseApplication = (interest) => COURSE_INTERESTS.has(interest);

function formHeading(interest) {
  if (interest === "BDaaS / Fractional Leadership") return "Strategy Audit Request";
  if (interest === "Strategy Audit") return "Strategy Audit Request";
  if (interest === "Custom Tech Build") return "Project Enquiry";
  if (isCourseApplication(interest)) return "Application for the Next Cohort";
  return "Academy & General Enquiry";
}

function submitLabel(interest) {
  if (interest === "BDaaS / Fractional Leadership" || interest === "Strategy Audit")
    return "Request Strategy Audit";
  if (interest === "Custom Tech Build") return "Send Project Brief";
  if (isCourseApplication(interest)) return "Submit Application";
  return "Send Enquiry";
}

const FUNDING_OPTIONS = [
  "Self-funded",
  "Employer / company will fund",
  "Not sure yet",
];

const TIMING_OPTIONS = [
  "Next 30 days",
  "Next 60–90 days",
  "Exploring — no fixed timeline",
];

export default function ContactFooter() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: interests[0],
    message: "",
    // Course-application fields (used only when interest is a course)
    role: "",
    city: "",
    outcome: "",
    funding: FUNDING_OPTIONS[0],
    timing: TIMING_OPTIONS[0],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    const handler = (e) => {
      const next = e.detail && e.detail.interest;
      if (next && interests.includes(next)) {
        setForm((p) => ({ ...p, interest: next }));
      }
    };
    window.addEventListener("sebyx-prefill", handler);
    return () => window.removeEventListener("sebyx-prefill", handler);
  }, []);

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    // For course applications, require at least the role + outcome fields
    if (isCourseApplication(form.interest)) {
      if (!form.role.trim() || !form.outcome.trim()) {
        toast.error("Please tell us your current role and desired outcome.");
        return;
      }
    }
    setSubmitting(true);
    try {
      // Pack application answers into the message field for the backend + email alert
      let messageOut = form.message;
      if (isCourseApplication(form.interest)) {
        messageOut = [
          `— APPLICATION FOR ${form.interest.toUpperCase()} —`,
          `Preferred city: ${form.city || "Not specified"}`,
          `Current role & years in sales: ${form.role}`,
          `Desired outcome: ${form.outcome}`,
          `Funded by: ${form.funding}`,
          `Preferred timing: ${form.timing}`,
          form.message ? `\nAdditional note: ${form.message}` : "",
        ]
          .filter(Boolean)
          .join("\n");
      }
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        company: form.company,
        interest: form.interest,
        message: messageOut,
      });
      track("lead_submitted", {
        interest: form.interest,
        type: isCourseApplication(form.interest) ? "application" : "enquiry",
      });
      setDone(true);
      toast.success(
        isCourseApplication(form.interest)
          ? "Application received. We'll review and get back within 2 business days."
          : "Enquiry received. We'll be in touch within one business day."
      );
      setForm({
        name: "",
        email: "",
        company: "",
        interest: interests[0],
        message: "",
        role: "",
        city: "",
        outcome: "",
        funding: FUNDING_OPTIONS[0],
        timing: TIMING_OPTIONS[0],
      });
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="relative border-t border-sebyx-border"
      style={{ backgroundColor: "var(--sebyx-footer-bg)" }}
    >
      <div className="px-6 md:px-12 lg:px-24 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Intro */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-sebyx-accent" />
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
                [09] Contact
              </span>
            </div>
            <h2
              data-testid="footer-headline"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-sebyx-text leading-[1.05]"
            >
              Architect your <br />
              <span className="text-sebyx-accent italic font-light">
                next breakthrough.
              </span>
            </h2>
            <p className="mt-6 font-body text-sebyx-muted leading-relaxed max-w-md">
              Drop the brief. We'll respond within one business day with a
              shortlist of next steps — strategy audit, fractional engagement,
              or academy onboarding.
            </p>

            <dl className="mt-12 space-y-8">
              <div>
                <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
                  Primary Operating Hubs
                </dt>
                <dd
                  data-testid="footer-primary-hubs"
                  className="font-display text-lg text-sebyx-text mt-2"
                >
                  Karnataka <span className="text-sebyx-accent">|</span>{" "}
                  Maharashtra <span className="text-sebyx-accent">|</span>{" "}
                  Gujarat
                </dd>
              </div>

              <div>
                <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
                  Strategic Reach
                </dt>
                <dd data-testid="footer-strategic-reach" className="mt-3">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-2.5 shrink-0" />
                      <span className="font-body text-base text-sebyx-text/90 leading-relaxed">
                        <span className="text-sebyx-text font-medium">
                          Pan-India
                        </span>{" "}
                        <span className="text-sebyx-muted">
                          (Academy, Coaching & Remote Fractional Leadership)
                        </span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-2.5 shrink-0" />
                      <span className="font-body text-base text-sebyx-text/90 leading-relaxed">
                        <span className="text-sebyx-text font-medium">
                          Global
                        </span>{" "}
                        <span className="text-sebyx-muted">
                          (GTM Strategy & Consulting)
                        </span>
                      </span>
                    </li>
                  </ul>
                </dd>
              </div>

              <div>
                <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
                  Engagements
                </dt>
                <dd className="font-display text-lg text-sebyx-text mt-2">
                  Fractional · Project-based · Academy
                </dd>
              </div>

              <div>
                <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
                  Direct Line
                </dt>
                <dd className="mt-3 flex flex-col gap-2">
                  <a
                    href="tel:+919972554702"
                    data-testid="footer-phone"
                    className="inline-flex items-center gap-3 font-display text-lg text-sebyx-text hover:text-sebyx-accent transition-colors w-fit"
                  >
                    <Phone size={16} className="text-sebyx-accent" />
                    +91 99725 54702
                  </a>
                  <a
                    href="mailto:contact@sebyx.com"
                    data-testid="footer-email"
                    className="inline-flex items-center gap-3 font-display text-lg text-sebyx-text hover:text-sebyx-accent transition-colors w-fit"
                  >
                    <Mail size={16} className="text-sebyx-accent" />
                    contact@sebyx.com
                  </a>
                </dd>
              </div>

              <div>
                <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
                  Connect
                </dt>
                <dd className="mt-3 flex items-center gap-3">
                  <a
                    href="https://www.linkedin.com/in/mandarprabhudesai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-linkedin"
                    aria-label="Mandar Prabhudesai on LinkedIn"
                    className="w-10 h-10 border border-sebyx-border text-sebyx-accent flex items-center justify-center hover:border-sebyx-accent hover:bg-sebyx-accent hover:text-sebyx-bg transition-colors"
                  >
                    <Linkedin size={16} />
                  </a>
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-sebyx-muted">
                    Mandar Prabhudesai
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="border border-sebyx-border bg-sebyx-surface p-8 lg:p-10 sebyx-corner relative"
            >
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-sebyx-border">
                <span
                  data-testid="contact-form-heading"
                  className="font-mono text-[11px] tracking-[0.25em] uppercase text-sebyx-accent"
                >
                  /// {formHeading(form.interest)}
                </span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                  Form 09.A
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  id="name"
                  label="Full Name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  testId="contact-input-name"
                />
                <Field
                  id="email"
                  label="Work Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  testId="contact-input-email"
                />
                <Field
                  id="company"
                  label="Company / Org"
                  value={form.company}
                  onChange={update("company")}
                  testId="contact-input-company"
                />
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="interest"
                    className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted"
                  >
                    Interest
                  </label>
                  <select
                    id="interest"
                    data-testid="contact-input-interest"
                    value={form.interest}
                    onChange={update("interest")}
                    className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-4 py-3 font-body focus:border-sebyx-accent outline-none transition-colors"
                  >
                    {interests.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Course application — 5 qualifying questions */}
              {isCourseApplication(form.interest) && (
                <div
                  data-testid="course-application-block"
                  className="mt-8 border-l-2 border-sebyx-accent bg-sebyx-bg/40 pl-5 py-5 pr-4"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent" />
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
                      Application Details · Invitation-only Programme
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field
                      id="city"
                      label="Preferred City"
                      value={form.city}
                      onChange={update("city")}
                      testId="contact-input-city"
                      placeholder="e.g., Bangalore"
                    />

                    <Field
                      id="role"
                      label="Current Role & Years in Sales"
                      required
                      value={form.role}
                      onChange={update("role")}
                      testId="contact-input-role"
                      placeholder="e.g., Sales associate at Tanishq, 8 months"
                    />

                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="funding"
                        className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted"
                      >
                        Funded By
                      </label>
                      <select
                        id="funding"
                        data-testid="contact-input-funding"
                        value={form.funding}
                        onChange={update("funding")}
                        className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-4 py-3 font-body focus:border-sebyx-accent outline-none transition-colors"
                      >
                        {FUNDING_OPTIONS.map((f) => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="timing"
                        className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted"
                      >
                        When Would You Like to Attend?
                      </label>
                      <select
                        id="timing"
                        data-testid="contact-input-timing"
                        value={form.timing}
                        onChange={update("timing")}
                        className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-4 py-3 font-body focus:border-sebyx-accent outline-none transition-colors"
                      >
                        {TIMING_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col gap-2">
                    <label
                      htmlFor="outcome"
                      className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted"
                    >
                      What is the ONE outcome you want from this programme? *
                    </label>
                    <textarea
                      id="outcome"
                      data-testid="contact-input-outcome"
                      rows={3}
                      required
                      value={form.outcome}
                      onChange={update("outcome")}
                      placeholder="e.g., I want to stop losing customers at the door and start closing 3 out of 10 walk-ins…"
                      className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-4 py-3 font-body resize-none focus:border-sebyx-accent outline-none transition-colors placeholder:text-sebyx-muted/60"
                    />
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-2">
                <label
                  htmlFor="message"
                  className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted"
                >
                  Brief (optional)
                </label>
                <textarea
                  id="message"
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your sector, target geographies, and the bottleneck you're solving for…"
                  className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-4 py-3 font-body resize-none focus:border-sebyx-accent outline-none transition-colors placeholder:text-sebyx-muted/60"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting || done}
                className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-sebyx-accent text-sebyx-bg font-semibold tracking-tight hover:bg-[#3FA097] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Submitting…
                  </>
                ) : done ? (
                  <>
                    <Check size={16} /> Received
                  </>
                ) : (
                  <>
                    {submitLabel(form.interest)}
                    <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Massive wordmark */}
        <div
          data-testid="footer-wordmark"
          aria-hidden
          className="mt-24 select-none leading-none font-display font-bold tracking-tighter text-sebyx-text/[0.08]"
          style={{ fontSize: "clamp(6rem, 22vw, 22rem)" }}
        >
          Sebyx<span className="text-sebyx-accent">.</span>
        </div>

        {/* Bottom row */}
        <div className="mt-6 pt-6 border-t border-sebyx-border flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sebyx-muted font-mono text-[10px] tracking-[0.2em] uppercase">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} Sebyx · Revenue Architecture
          </span>
          <span>Engineered across Karnataka × Maharashtra × Gujarat</span>
          <span>v1.0 · Sebyx Operating System</span>
        </div>

        {/* Legal links */}
        <nav
          data-testid="footer-legal-nav"
          className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sebyx-muted font-mono text-[10px] tracking-[0.2em] uppercase"
        >
          <a
            href="/terms-of-service"
            data-testid="footer-link-terms"
            className="hover:text-sebyx-accent transition-colors"
          >
            Terms of Service
          </a>
          <a
            href="/privacy-policy"
            data-testid="footer-link-privacy"
            className="hover:text-sebyx-accent transition-colors"
          >
            Privacy Policy
          </a>
          <a
            href="/refund-policy"
            data-testid="footer-link-refund"
            className="hover:text-sebyx-accent transition-colors"
          >
            Refund Policy
          </a>
          <a
            href="/cancellation-policy"
            data-testid="footer-link-cancellation"
            className="hover:text-sebyx-accent transition-colors"
          >
            Cancellation Policy
          </a>
        </nav>
      </div>
    </footer>
  );
}

function Field({ id, label, value, onChange, type = "text", required, testId, placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted"
      >
        {label}
        {required && <span className="text-sebyx-accent ml-1">*</span>}
      </label>
      <input
        id={id}
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-4 py-3 font-body focus:border-sebyx-accent outline-none transition-colors placeholder:text-sebyx-muted/60"
      />
    </div>
  );
}
